import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { SwatchKind } from "@/components/primitives";
import { registerChannel, unregisterChannel } from "@/lib/realtimeChannels";

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  body: string;
  created_at: string;
}

export interface ConversationListRow {
  id: string;
  scan_id: string | null;
  buyer_id: string;
  seller_id: string;
  last_message_at: string;
  // other party snapshot
  other_handle: string;
  other_display_name: string | null;
  other_avatar: SwatchKind;
  other_id: string;
  // listing snapshot
  scan_title: string;
  scan_photo: string | null;
  scan_img: string;
  // last message preview + unread state
  last_body: string | null;
  last_sender_id: string | null;
  unread: boolean;
}

/**
 * Find or create a conversation between the signed-in buyer and the seller of
 * a given scan. Returns the conversation id.
 */
export async function ensureConversation(
  buyerId: string,
  sellerId: string,
  scanId?: string | null,
): Promise<string | null> {
  if (buyerId === sellerId) return null;

  if (scanId) {
    // Scan-linked: find by (scan_id, buyer, seller) — order matters for existing rows.
    const { data: existing } = await supabase
      .from("conversations")
      .select("id")
      .eq("scan_id", scanId)
      .eq("buyer_id", buyerId)
      .eq("seller_id", sellerId)
      .maybeSingle();
    if (existing) return existing.id;
    const { data: created, error } = await supabase
      .from("conversations")
      .insert({ scan_id: scanId, buyer_id: buyerId, seller_id: sellerId })
      .select("id")
      .single();
    if (error) { console.warn("ensureConversation failed:", error); return null; }
    return created.id;
  }

  // General DM — find existing in either direction, then create if absent.
  const { data: existing } = await supabase
    .from("conversations")
    .select("id")
    .is("scan_id", null)
    .or(`and(buyer_id.eq.${buyerId},seller_id.eq.${sellerId}),and(buyer_id.eq.${sellerId},seller_id.eq.${buyerId})`)
    .maybeSingle();
  if (existing) return existing.id;

  const { data: created, error } = await supabase
    .from("conversations")
    .insert({ scan_id: null, buyer_id: buyerId, seller_id: sellerId })
    .select("id")
    .single();
  if (error) { console.warn("ensureConversation (general) failed:", error); return null; }
  return created.id;
}

/** Inbox listing — both as buyer and as seller. */
export function useConversations() {
  const { user } = useAuth();
  const [rows, setRows] = useState<ConversationListRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setRows([]); setLoading(false); return; }
    setLoading(true);
    const { data: convos } = await supabase
      .from("conversations")
      .select("id,scan_id,buyer_id,seller_id,last_message_at")
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
      .order("last_message_at", { ascending: false })
      .limit(60);
    if (!convos || convos.length === 0) { setRows([]); setLoading(false); return; }

    const ids = convos.map((c) => c.id);
    const otherIds = Array.from(new Set(convos.map((c) => (c.buyer_id === user.id ? c.seller_id : c.buyer_id))));
    const scanIds = Array.from(new Set(convos.map((c) => c.scan_id).filter((id): id is string => !!id)));

    const [{ data: profiles }, { data: scans }, { data: reads }, { data: lastMsgs }] = await Promise.all([
      (supabase as any).from("profiles_public").select("id,handle,display_name,avatar").in("id", otherIds),
      supabase.from("scans").select("id,title,photos,img").in("id", scanIds),
      supabase.from("message_reads").select("conversation_id,last_read_at").eq("user_id", user.id).in("conversation_id", ids),
      // For each conversation, the most recent message (we fetch a window then reduce client-side)
      supabase.from("messages").select("conversation_id,sender_id,body,created_at").in("conversation_id", ids).order("created_at", { ascending: false }).limit(ids.length * 5),
    ]);

    const pMap = new Map(
      ((profiles ?? []) as Array<{ id: string; handle: string; display_name: string | null; avatar: string }>)
        .map((p) => [p.id, p] as const)
    );
    const sMap = new Map((scans ?? []).map((s) => [s.id, s]));
    const rMap = new Map((reads ?? []).map((r) => [r.conversation_id, r.last_read_at]));
    const lastByConvo = new Map<string, { sender_id: string; body: string; created_at: string }>();
    for (const m of lastMsgs ?? []) {
      if (!lastByConvo.has(m.conversation_id)) {
        lastByConvo.set(m.conversation_id, { sender_id: m.sender_id, body: m.body, created_at: m.created_at });
      }
    }

    const enriched: ConversationListRow[] = convos.map((c) => {
      const otherId = c.buyer_id === user.id ? c.seller_id : c.buyer_id;
      const p = pMap.get(otherId);
      const s = c.scan_id ? sMap.get(c.scan_id) : undefined;
      const last = lastByConvo.get(c.id);
      const lastRead = rMap.get(c.id);
      const unread = !!last && last.sender_id !== user.id && (!lastRead || new Date(last.created_at) > new Date(lastRead));
      return {
        id: c.id,
        scan_id: c.scan_id,
        buyer_id: c.buyer_id,
        seller_id: c.seller_id,
        last_message_at: c.last_message_at,
        other_id: otherId,
        other_handle: p?.handle ?? "someone",
        other_display_name: p?.display_name ?? null,
        other_avatar: ((p?.avatar ?? "cream") as SwatchKind),
        scan_title: s?.title ?? "Listing",
        scan_photo: (s?.photos && s.photos.length > 0) ? s.photos[0] : null,
        scan_img: (s?.img ?? "olive") as string,
        last_body: last?.body ?? null,
        last_sender_id: last?.sender_id ?? null,
        unread,
      };
    });
    setRows(enriched);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
    if (!user) return;
    // Realtime: refresh inbox when any new message touches us
    const key = `inbox-${user.id}-${Math.random().toString(36).slice(2, 8)}`;
    registerChannel(key, () =>
      supabase
        .channel(key)
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, () => { void load(); })
        .subscribe()
    );
    return () => { unregisterChannel(key); };
  }, [user, load]);

  return { rows, loading, refresh: load };
}

/** Single thread — messages + send + mark-read. */
export function useThread(conversationId: string | null) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!conversationId || !user) { setMessages([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true })
        .limit(200);
      if (cancelled) return;
      if (data) setMessages(data as Message[]);
      setLoading(false);
      // Mark read
      void supabase.from("message_reads").upsert(
        { conversation_id: conversationId, user_id: user.id, last_read_at: new Date().toISOString() },
        { onConflict: "conversation_id,user_id" },
      ).then(() => {
        // Fallback for environments where message_reads isn't in the
        // supabase_realtime publication: dispatch a window event so the
        // dock badge hook (separate instance from this thread component)
        // can refresh without round-tripping through realtime.
        window.dispatchEvent(new Event("fig:message-read"));
      });
    })();

    const key = `thread-${conversationId}-${Math.random().toString(36).slice(2, 8)}`;
    registerChannel(key, () =>
      supabase
        .channel(key)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${conversationId}` },
          (payload) => {
            const m = payload.new as Message;
            setMessages((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
            // If message is from the other party, bump read marker
            if (m.sender_id !== user.id) {
              void supabase.from("message_reads").upsert(
                { conversation_id: conversationId, user_id: user.id, last_read_at: new Date().toISOString() },
                { onConflict: "conversation_id,user_id" },
              );
            }
          },
        )
        .subscribe()
    );

    return () => { cancelled = true; unregisterChannel(key); };
  }, [conversationId, user]);

  const send = async (body: string): Promise<boolean> => {
    if (!user || !conversationId) return false;
    const trimmed = body.trim();
    if (!trimmed || sending) return false;
    if (trimmed.length > 2000) return false;
    setSending(true);
    const { data, error } = await supabase
      .from("messages")
      .insert({ conversation_id: conversationId, sender_id: user.id, body: trimmed })
      .select()
      .single();
    setSending(false);
    if (error) {
      console.warn("send message failed:", error);
      return false;
    }
    if (data) setMessages((prev) => prev.some((x) => x.id === data.id) ? prev : [...prev, data as Message]);
    return true;
  };

  return { messages, loading, sending, send };
}

/** Lightweight unread count for the dock badge. */
export function useUnreadInbox() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) { setCount(0); return; }
    // Pull all my conversations + my read marks; compute unread client-side.
    const { data: convos } = await supabase
      .from("conversations")
      .select("id,buyer_id,seller_id,last_message_at")
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
      .limit(100);
    if (!convos || convos.length === 0) { setCount(0); return; }
    const ids = convos.map((c) => c.id);
    const [{ data: reads }, { data: lasts }] = await Promise.all([
      supabase.from("message_reads").select("conversation_id,last_read_at").eq("user_id", user.id).in("conversation_id", ids),
      supabase.from("messages").select("conversation_id,sender_id,created_at").in("conversation_id", ids).order("created_at", { ascending: false }).limit(ids.length * 4),
    ]);
    const rMap = new Map((reads ?? []).map((r) => [r.conversation_id, r.last_read_at]));
    const seen = new Set<string>();
    let unread = 0;
    for (const m of lasts ?? []) {
      if (seen.has(m.conversation_id)) continue;
      seen.add(m.conversation_id);
      if (m.sender_id === user.id) continue;
      const lastRead = rMap.get(m.conversation_id);
      if (!lastRead || new Date(m.created_at) > new Date(lastRead)) unread += 1;
    }
    setCount(unread);
  }, [user]);

  useEffect(() => {
    void refresh();
    if (!user) return;
    const key = `inbox-badge-${user.id}-${Math.random().toString(36).slice(2, 8)}`;
    registerChannel(key, () =>
      supabase
        .channel(key)
        // Bump on new messages — the obvious trigger.
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, () => { void refresh(); })
        // Also bump when my own read marker advances. useThread upserts
        // message_reads when the user opens a conversation, but without
        // this listener the badge stayed at the old count because the
        // count is computed against that table. Filter to my user_id so
        // we don't refire for everyone else's read events.
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "message_reads", filter: `user_id=eq.${user.id}` },
          () => { void refresh(); },
        )
        .subscribe()
    );
    const onLocalRead = () => { void refresh(); };
    window.addEventListener("fig:message-read", onLocalRead);
    return () => {
      unregisterChannel(key);
      window.removeEventListener("fig:message-read", onLocalRead);
    };
  }, [user, refresh]);

  return { count, refresh };
}